// Offline Configuration Diagnostic Component
// Shows current offline configuration and allows runtime testing

import React, { useEffect, useState } from 'react';
import { offlineConfig } from '../../config/offlineConfig';
import { useUser } from '../../contextapi/UserContext';
import { ALTZERO_TABLES, getOfflineDB } from '../../utils/offlineDB';

interface OfflineStatus {
  serviceWorkerRegistered: boolean;
  serviceWorkerActive: boolean;
  indexedDBSupported: boolean;
  indexedDBInitialized: boolean;
  cacheNames: string[];
  totalCachedItems: number;
  onlineStatus: boolean;
  dbCounts?: { [key: string]: number };
}

export const OfflineConfigDiagnostic: React.FC = () => {
  const { user } = useUser();
  const [status, setStatus] = useState<OfflineStatus>({
    serviceWorkerRegistered: false,
    serviceWorkerActive: false,
    indexedDBSupported: false,
    indexedDBInitialized: false,
    cacheNames: [],
    totalCachedItems: 0,
    onlineStatus: navigator.onLine,
    dbCounts: {}
  });

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    checkOfflineStatus();

    // Listen for online/offline changes
    const handleOnline = () => setStatus(prev => ({ ...prev, onlineStatus: true }));
    const handleOffline = () => setStatus(prev => ({ ...prev, onlineStatus: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [user]);

  const checkOfflineStatus = async () => {
    try {
      // Check Service Worker
      let swRegistered = false;
      let swActive = false;
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration('/');
        swRegistered = !!registration;
        swActive = !!(registration && registration.active);
      }

      // Check IndexedDB
      const idbSupported = 'indexedDB' in window;
      let idbInitialized = false;
      const dbCounts: { [key: string]: number } = {};

      if (idbSupported) {
        try {
          const databases = await indexedDB.databases();
          idbInitialized = databases.some(db => db.name?.includes('altzero'));

          if (user) {
            const offlineDB = getOfflineDB(user.id);
            // We can't easily check if DB is open without opening it, 
            // but we can try to count if initialized
            if (idbInitialized) {
              await offlineDB.ensureReady();
              for (const table of ALTZERO_TABLES) {
                try {
                  const records = await offlineDB.getAll(table);
                  dbCounts[table] = records.length;
                } catch (e) {
                  dbCounts[table] = 0;
                }
              }
            }
          }
        } catch (error) {
          console.warn('Could not check IndexedDB databases:', error);
        }
      }

      // Check Cache Storage
      let cacheNames: string[] = [];
      let totalCachedItems = 0;
      if ('caches' in window) {
        try {
          cacheNames = await caches.keys();
          for (const cacheName of cacheNames) {
            const cache = await caches.open(cacheName);
            const keys = await cache.keys();
            totalCachedItems += keys.length;
          }
        } catch (error) {
          console.warn('Could not check cache storage:', error);
        }
      }

      setStatus({
        serviceWorkerRegistered: swRegistered,
        serviceWorkerActive: swActive,
        indexedDBSupported: idbSupported,
        indexedDBInitialized: idbInitialized,
        cacheNames,
        totalCachedItems,
        onlineStatus: navigator.onLine,
        dbCounts
      });
    } catch (error) {
      console.error('Failed to check offline status:', error);
    }
  };

  const config = offlineConfig.getConfig();

  const getStatusIcon = (enabled: boolean, working: boolean = true) => {
    if (!enabled) return '🚫';
    return working ? '✅' : '❌';
  };

  const getStatusText = (enabled: boolean, working: boolean = true) => {
    if (!enabled) return 'Disabled';
    return working ? 'Working' : 'Error';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          🔧 Offline Configuration Status
        </h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-sm text-blue-600 hover:text-blue-800"
        >
          {isExpanded ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {/* Quick Status Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl mb-1">
            {status.onlineStatus ? '🌐' : '📱'}
          </div>
          <div className="text-sm font-medium">
            {status.onlineStatus ? 'Online' : 'Offline'}
          </div>
        </div>

        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl mb-1">
            {getStatusIcon(config.enableOfflineMode)}
          </div>
          <div className="text-sm font-medium">
            Offline Mode
          </div>
          <div className="text-xs text-gray-600">
            {getStatusText(config.enableOfflineMode)}
          </div>
        </div>

        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl mb-1">
            {getStatusIcon(config.enableServiceWorker, status.serviceWorkerActive)}
          </div>
          <div className="text-sm font-medium">
            Service Worker
          </div>
          <div className="text-xs text-gray-600">
            {getStatusText(config.enableServiceWorker, status.serviceWorkerActive)}
          </div>
        </div>

        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl mb-1">
            {getStatusIcon(config.enableIndexedDB, status.indexedDBInitialized)}
          </div>
          <div className="text-sm font-medium">
            IndexedDB
          </div>
          <div className="text-xs text-gray-600">
            {getStatusText(config.enableIndexedDB, status.indexedDBInitialized)}
          </div>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Detailed Configuration */}
          <div className="space-y-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">📋 Configuration Details</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Cache Strategy:</span>
                  <span className="font-mono">{config.cacheStrategy}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sync Interval:</span>
                  <span className="font-mono">{config.syncInterval}ms</span>
                </div>
                <div className="flex justify-between">
                  <span>Max Retry Attempts:</span>
                  <span className="font-mono">{config.maxRetryAttempts}</span>
                </div>
                <div className="flex justify-between">
                  <span>Debug Logs:</span>
                  <span className="font-mono">{config.enableDebugLogs ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Environment:</span>
                  <span className="font-mono">{config.isDevelopment ? 'Development' : 'Production'}</span>
                </div>
              </div>
            </div>

            {/* Feature Status */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">🎯 Feature Status</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Offline Auth:</span>
                  <span>{config.enableOfflineAuth ? '✅ Enabled' : '❌ Disabled'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Offline Data:</span>
                  <span>{config.enableOfflineData ? '✅ Enabled' : '❌ Disabled'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Offline Assets:</span>
                  <span>{config.enableOfflineAssets ? '✅ Enabled' : '❌ Disabled'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Background Sync:</span>
                  <span>{config.enableBackgroundSync ? '✅ Enabled' : '❌ Disabled'}</span>
                </div>
              </div>
            </div>

            {/* Runtime Status */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">⚡ Runtime Status</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Service Worker Registered:</span>
                  <span>{status.serviceWorkerRegistered ? '✅ Yes' : '❌ No'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Service Worker Active:</span>
                  <span>{status.serviceWorkerActive ? '✅ Yes' : '❌ No'}</span>
                </div>
                <div className="flex justify-between">
                  <span>IndexedDB Supported:</span>
                  <span>{status.indexedDBSupported ? '✅ Yes' : '❌ No'}</span>
                </div>
                <div className="flex justify-between">
                  <span>IndexedDB Initialized:</span>
                  <span>{status.indexedDBInitialized ? '✅ Yes' : '❌ No'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cache Names:</span>
                  <span className="font-mono">{status.cacheNames.length} caches</span>
                </div>
                <div className="flex justify-between">
                  <span>Cached Assets:</span>
                  <span className="font-mono">{status.totalCachedItems} items</span>
                </div>
                {status.indexedDBInitialized && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="text-xs font-medium text-gray-500 mb-1">IndexedDB Records:</div>
                    <div className="space-y-1">
                      {Object.entries(status.dbCounts || {}).map(([table, count]) => (
                        <div key={table} className="flex justify-between text-xs">
                          <span>{table}:</span>
                          <span className="font-mono">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div>
              <h4 className="font-medium text-gray-900 mb-2">🔧 Actions</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={checkOfflineStatus}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm hover:bg-blue-200"
                >
                  Refresh Status
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm hover:bg-gray-200"
                >
                  Reload Page
                </button>
                <button
                  onClick={() => console.log('Offline Config:', offlineConfig.getConfig())}
                  className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm hover:bg-green-200"
                >
                  Log Config
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
